import styled from "@emotion/styled";
import { IconButton, Stack } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Pencil } from "@phosphor-icons/react";
import { Button } from "components/UI";
import { useChurchStore } from "features/churches";
import {
  useBudgetYearsPaginated,
  useFinanceSettingsStore
} from "features/finances";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { UpdateBudgetYearModal } from "../Modals";

const Container = styled.div`
  grid-column: 2 / 3;
  grid-row: 1 / 3;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const Title = styled.h6`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
`;

const PrimaryActions = styled.div`
  display: flex;
  gap: 8px;
`;

export const BudgetYearsTable = ({ onboarding }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [budgetYear, setBudgetYear] = useState(null);
  const openCreateBudgetYearModal = useFinanceSettingsStore(
    state => state.openCreateBudgetYearModal
  );
  const openUpdateBudgetYearModal = useFinanceSettingsStore(
    state => state.openUpdateBudgetYearModal
  );
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const budgetYearsQuery = useBudgetYearsPaginated({
    churchId: selectedChurch?.id,
    page,
    pageSize
  });

  const columns = [
    {
      field: "title",
      headerName: "Title",
      flex: 1
    },
    {
      field: "start_date",
      headerName: "Start Date",
      flex: 1
    },
    {
      field: "end_date",
      headerName: "End Date",
      flex: 1
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      hide: onboarding,
      renderCell: ({ row }) => {
        return (
          <Stack direction="row">
            <IconButton
              onClick={() => {
                setBudgetYear(row);
                openUpdateBudgetYearModal();
              }}
            >
              <Pencil />
            </IconButton>
          </Stack>
        );
      }
    }
  ];

  return (
    <Container>
      <UpdateBudgetYearModal data={budgetYear} />
      {!onboarding && (
        <Stack alignItems="flex-start" spacing={4}>
          <Header>
            <Title>Budget Years</Title>
            <PrimaryActions>
              <Button onClick={openCreateBudgetYearModal}>
                Add Budget Year
              </Button>
            </PrimaryActions>
          </Header>
        </Stack>
      )}
      <DataGrid
        autoHeight
        rows={budgetYearsQuery.data?.result?.data || []}
        rowCount={budgetYearsQuery.data?.result?.total || 0}
        loading={budgetYearsQuery.isLoading}
        columns={columns}
        pagination
        rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
        density="comfortable"
        getRowId={row => row.id}
        disableColumnMenu
        disableSelectionOnClick
        paginationMode="server"
        page={page - 1}
        pageSize={pageSize}
        onPageChange={newPage => setPage(newPage + 1)}
        onPageSizeChange={newPageSize => setPageSize(newPageSize)}
        localeText={{
          noRowsLabel: t("teams.teamProfile.NoRows"),
          MuiTablePagination: {
            labelRowsPerPage: t("teams.teamProfile.RowsPerPage")
          }
        }}
      />
    </Container>
  );
};
